import { QuestionType } from '@app/src/types/reporting'
import type Assessment from '@app/src/types/assessment'
import type {
  AssessmentRule,
  AssessmentSection,
  AssessmentTemplateWithClassificationRules,
  QuestionAnswerClassificationResult,
  QuestionAnswerRank,
  ResponseItem,
} from '@app/src/types/resourceExplorer'
import { AnswerRuleType, AssessmentType } from '@app/src/types/resourceExplorer'

export interface AnswerRow {
  item: ResponseItem
  rules: AssessmentRule[]
  displayNumber?: string
}

export interface AnswerSection {
  key: string
  name?: string
  level?: number
  rows: AnswerRow[]
}

export interface OptionScore {
  value: string
  isSelected: boolean
  isRewarded: boolean
  points: number
}

export interface FileScore {
  hasFile: boolean
  isRewarded: boolean
  points: number
}

export interface ClassificationScore {
  result: QuestionAnswerClassificationResult
  points: number
}

export interface SelectedOptionsCount {
  selected: number
  total: number
}

interface SectionSpec {
  key: string
  name?: string
  sectionNumber?: number
  questionIds: number[]
  numberPrefix?: string
}

const getQuestionId = (item: ResponseItem): number | undefined =>
  item.requestItem.template?.id ?? item.requestItem.questionId

const bySectionNumber = (a: AssessmentSection, b: AssessmentSection) =>
  a.assessmentSectionNumber - b.assessmentSectionNumber

const byRequestItemOrder = (a: ResponseItem, b: ResponseItem) => (a.requestItem.order ?? 0) - (b.requestItem.order ?? 0)

const sumPoints = (rules: AssessmentRule[]) => rules.reduce((total, rule) => total + rule.points, 0)

const unique = <T>(values: T[]): T[] => [...new Set(values)]

export const getSectionLevel = (assessment: Assessment, sectionNumber: number | undefined): number | undefined => {
  if (sectionNumber === undefined) return undefined
  const levels = [
    assessment.section1Level,
    assessment.section2Level,
    assessment.section3Level,
    assessment.section4Level,
    assessment.section5Level,
    assessment.section6Level,
  ]
  return levels[sectionNumber - 1]
}

export const getTemplateSections = (assessment: Assessment): AssessmentSection[] =>
  [...(assessment.assessmentTemplate.sections ?? [])].sort(bySectionNumber)

export const getLatestResponseItemPerQuestion = (items: ResponseItem[]): ResponseItem[] => {
  const newestFirst = [...items].sort(
    (a, b) => new Date(b.response?.submittedAt ?? 0).getTime() - new Date(a.response?.submittedAt ?? 0).getTime(),
  )
  const latestByQuestion = new Map<number, ResponseItem>()
  newestFirst.forEach(item => {
    const questionId = getQuestionId(item)
    if (questionId && !latestByQuestion.has(questionId)) latestByQuestion.set(questionId, item)
  })
  return [...latestByQuestion.values()]
}

const getAllTemplateRules = (assessment: Assessment): AssessmentRule[] => {
  const rules = [
    ...(assessment.assessmentTemplate.rules ?? []),
    ...getTemplateSections(assessment).flatMap(section => section.rules ?? []),
  ]
  return [...new Map(rules.map(rule => [rule.id, rule])).values()]
}

const getSectionSpecs = (
  assessment: Assessment,
  templateWithRules: AssessmentTemplateWithClassificationRules | undefined,
): SectionSpec[] => {
  const assessmentType = assessment.assessmentTemplate.assessmentType

  if (assessmentType === AssessmentType.BaselineAssessment) {
    return getTemplateSections(assessment).map(section => ({
      key: String(section.id),
      name: section.name,
      sectionNumber: section.assessmentSectionNumber,
      questionIds: unique((section.rules ?? []).map(rule => rule.questionId)),
    }))
  }

  if (!templateWithRules) return []

  if (assessmentType === AssessmentType.Flexible && templateWithRules.sections?.length) {
    return [...templateWithRules.sections].sort(bySectionNumber).map((section, index) => ({
      key: String(section.id),
      name: section.name,
      sectionNumber: section.assessmentSectionNumber,
      questionIds: (section.questions ?? []).map(question => question.questionId),
      numberPrefix: `${index + 1}.`,
    }))
  }

  return [{ key: 'all', questionIds: (templateWithRules.questions ?? []).map(question => question.id) }]
}

export const buildAnswerSections = (
  assessment: Assessment,
  responseItems: ResponseItem[],
  templateWithRules: AssessmentTemplateWithClassificationRules | undefined,
): AnswerSection[] => {
  const allRules = getAllTemplateRules(assessment)
  const latestByQuestion = new Map(
    getLatestResponseItemPerQuestion(responseItems).map(item => [getQuestionId(item), item]),
  )

  return getSectionSpecs(assessment, templateWithRules).map(spec => {
    const items = spec.questionIds
      .flatMap(questionId => latestByQuestion.get(questionId) ?? [])
      .sort(byRequestItemOrder)

    return {
      key: spec.key,
      name: spec.name,
      level: getSectionLevel(assessment, spec.sectionNumber),
      rows: items.map((item, index) => ({
        item,
        rules: allRules.filter(rule => rule.questionId === getQuestionId(item)),
        displayNumber: spec.numberPrefix && `${spec.numberPrefix}${index + 1}`,
      })),
    }
  })
}

export const findPrimaryResponseItem = (assessment: Assessment, responseItems: ResponseItem[]) =>
  responseItems.find(
    item => item.response?.request?.questionnaireTemplateId === assessment.assessmentTemplate.questionnaireTemplateId,
  )

export const getSelectedOptionsCount = (item: ResponseItem): SelectedOptionsCount | undefined => {
  if (item.requestItem.questionTypeId !== QuestionType.Options) return undefined
  const total = item.requestItem.template?.requestCollection?.items.length ?? 0
  const selected = item.selectedOptions?.length ?? 0
  if (total === 0 || selected === 0) return undefined
  return { selected, total }
}

export const getOptionScores = (item: ResponseItem, rules: AssessmentRule[]): OptionScore[] => {
  const options = item.requestItem.template?.requestCollection?.items ?? []
  const selected = new Set(item.selectedOptions ?? [])

  return options.map(option => {
    const matchingRules = rules.filter(rule => rule.value.trim().toLowerCase() === option.value.trim().toLowerCase())
    return {
      value: option.value,
      isSelected: selected.has(option.value),
      isRewarded: matchingRules.length > 0,
      points: sumPoints(matchingRules),
    }
  })
}

export const getFileScore = (item: ResponseItem, rules: AssessmentRule[]): FileScore => {
  const hasFile = Boolean(item.uploadReferences?.length)
  const matchingRules = rules.filter(
    rule => rule.ruleType === AnswerRuleType.FileUploaded && rule.value === (hasFile ? '1' : '0'),
  )
  return { hasFile, isRewarded: matchingRules.length > 0, points: sumPoints(matchingRules) }
}

const getPointsForRank = (rules: AssessmentRule[], rank: QuestionAnswerRank | undefined) =>
  rules.find(rule => rule.value === rank)?.points ?? 0

export const getClassificationScores = (item: ResponseItem, rules: AssessmentRule[]): ClassificationScore[] =>
  (item.questionAnswerClassificationResults ?? []).map(result => ({
    result,
    points: getPointsForRank(rules, result.questionAnswerClassificationRule?.rank ?? result.rank),
  }))
